import {
    AddOutline,
    Box,
    Button,
    Delete,
    Divider,
    Heading,
    IconButton,
    Modal,
    SearchSelect,
    Select,
    showToast,
    StaticTable,
    SubPageLayout,
    TextInput,
    useSubPage,
} from "@bcc-ui/ui-components";
import { Form, Formik, FormikConfig } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";

import {
    addRow,
    AssetDetailFormValues,
    buildValidationSchema,
    FormValues,
    isFieldErrors,
    isSameAssetKey,
    Result,
    useExistingAssetKeys,
} from "components/asset-management-system/assetFormUtils";
import { GroupSelect } from "components/asset-management-system/custom-views/GroupSelect";
import style from "components/asset-management-system/manage-assets/manage-asset.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { useIsMobileViewport } from "components/navigation/hooks/useIsMobileViewport";
import { AssetPathKey, MANDATORY_ASSET_FIELDS, resolveAssetPathKey } from "domain/asset-management/assetPathMapping";
import {
    ASSET_IDENTITY_AND_CLASSIFICATION,
    ASSET_OVERVIEW,
    ASSET_RISK_AND_LIFECYCLE,
    Assets,
    AUTOPILOT,
    BATTERY,
    FINANCIAL_AND_PROCUREMENT,
    GRADING_ACTION_DETAIL_KEY,
    GRADING_ACTION_KEYS,
    HARDWARE_SPECIFICATIONS,
    NETWORK_AND_IDENTITY,
    SECURITY_AND_ENDPOINT_HEALTH,
    STORAGE,
    WARRANTY_STATUS,
} from "domain/asset-management/assets";
import { ASSET_TYPE_MAX_LENGTH, CUSTOM_ASSET_FIELD_MAX_LENGTH, INPUT_MAX_LENGTH } from "domain/globalConstants";
import { Asset, assetService } from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { resolveColor } from "utils/commonFunctions";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface SectionGroup {
    titleKey: string;
    keys: AssetPathKey[];
}

const SECTION_GROUPS: SectionGroup[] = [
    { titleKey: "AssetManagement.warrantyStatus", keys: WARRANTY_STATUS },
    { titleKey: "AssetManagement.sidePanel.sections.riskAndLifecycleStatus", keys: ASSET_RISK_AND_LIFECYCLE },
    {
        titleKey: "AssetManagement.editAssetSubpage.sections.identityAndClassification",
        keys: ASSET_IDENTITY_AND_CLASSIFICATION,
    },
    { titleKey: "AssetManagement.editAssetSubpage.sections.hardwareSpecifications", keys: HARDWARE_SPECIFICATIONS },
    { titleKey: "AssetManagement.editAssetSubpage.sections.storage", keys: STORAGE },
    { titleKey: "AssetManagement.editAssetSubpage.sections.networkAndIdentity", keys: NETWORK_AND_IDENTITY },
    { titleKey: "AssetManagement.editAssetSubpage.sections.battery", keys: BATTERY },
    { titleKey: "AssetManagement.editAssetSubpage.sections.autopilotAndMdm", keys: AUTOPILOT },
    {
        titleKey: "AssetManagement.editAssetSubpage.sections.securityAndEndpointHealth",
        keys: SECURITY_AND_ENDPOINT_HEALTH,
    },
    {
        titleKey: "AssetManagement.editAssetSubpage.sections.financialAndProcurement",
        keys: FINANCIAL_AND_PROCUREMENT,
    },
];

const MANDATORY_KEYS = new Set<string>(MANDATORY_ASSET_FIELDS);
const ALL_GROUPED_KEYS = new Set<string>([
    ...SECTION_GROUPS.flatMap((group) => group.keys),
    ...MANDATORY_ASSET_FIELDS,
    ...ASSET_OVERVIEW,
]);

const BLANCCO_INTELLIGENCE_AND_GRADING_KEYS = new Set<string>([
    AssetPathKey.BLANCCO_DURATION,
    AssetPathKey.BLANCCO_ERASABILITY,
    AssetPathKey.BLANCCO_GRADE,
]);

const RISK_SCORE_KEYS = new Set<string>([
    "risk_score_by_data_bearing",
    "risk_score_by_lock_state",
    "risk_score_by_erasure_report",
]);

const EDITABLE_FIELDS: {
    pathKey: AssetPathKey;
    name: keyof FormValues;
    testId: { itself: string; errorLabel: string };
    maxLength: number;
}[] = [
    {
        pathKey: AssetPathKey.SERIAL_NUMBER,
        name: "serialNumber",
        testId: testIds.workArea.asset.manageAssetView.serialNumberInput,
        maxLength: INPUT_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.ASSET_TYPE,
        name: "assetType",
        testId: testIds.workArea.asset.manageAssetView.assetTypeInput,
        maxLength: ASSET_TYPE_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.ASSET_TAG,
        name: "assetTag",
        testId: testIds.workArea.asset.manageAssetView.assetTagInput,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.MANUFACTURER,
        name: "manufacturer",
        testId: testIds.workArea.asset.manageAssetView.manufacturer,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.MODEL,
        name: "model",
        testId: testIds.workArea.asset.manageAssetView.model,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.ASSET_STATE,
        name: "assetState",
        testId: testIds.workArea.asset.manageAssetView.lifecycleStatus,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.ASSIGNED_TO,
        name: "assignedTo",
        testId: testIds.workArea.asset.manageAssetView.owner,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.DEPARTMENT,
        name: "department",
        testId: testIds.workArea.asset.manageAssetView.department,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.LOCATION,
        name: "location",
        testId: testIds.workArea.asset.manageAssetView.location,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
    {
        pathKey: AssetPathKey.REGION,
        name: "region",
        testId: testIds.workArea.asset.manageAssetView.region,
        maxLength: CUSTOM_ASSET_FIELD_MAX_LENGTH,
    },
];

const PARENT_ASSET_ID_KEY = "parent_asset_id";

const EMPTY_FORM_VALUES: FormValues = {
    assetId: "",
    serialNumber: "",
    assetType: "",
    assetTag: "",
    manufacturer: "",
    model: "",
    assetState: "",
    assignedTo: "",
    location: "",
    department: "",
    region: "",
    parentAssetId: "",
    assetDetails: [],
};

const toFormValues = (asset: Asset, resolvedParentAssetId?: string): FormValues => {
    const details = asset.assetDetails ?? [];
    const modelFromDetails = details.find((d) => d.key === AssetPathKey.MODEL)?.value;
    const parentAssetIdFromDetails = details.find((d) => d.key === PARENT_ASSET_ID_KEY)?.value;
    const resolvedParentAssetIdValue = parentAssetIdFromDetails ?? resolvedParentAssetId;

    const assetDetailsArray: AssetDetailFormValues[] = details
        .filter((detail) => detail.key !== AssetPathKey.MODEL)
        .map((detail) => ({ ...detail, isNew: false }));

    if (resolvedParentAssetIdValue && !parentAssetIdFromDetails) {
        assetDetailsArray.push({ key: PARENT_ASSET_ID_KEY, value: resolvedParentAssetIdValue, isNew: false });
    }

    return {
        assetId: asset.assetId ?? "",
        serialNumber: asset.serialNumber ?? "",
        assetType: asset.assetType ?? "",
        assetTag: asset.assetTag ?? "",
        manufacturer: asset.manufacturer ?? "",
        model: asset.model ?? modelFromDetails ?? "",
        assetState: (asset.assetState as string) ?? "",
        assignedTo: asset.assignedTo ?? "",
        location: asset.location ?? "",
        department: (asset.department as string) ?? "",
        region: (asset.region as string) ?? "",
        parentAssetId: resolvedParentAssetIdValue ?? "",
        assetDetails: assetDetailsArray,
    };
};

const toAssets = (values: FormValues): Assets => {
    const hasParentAssetIdInDetails = values.assetDetails.some((d) => d.key === PARENT_ASSET_ID_KEY);
    return {
        assetId: values.assetId,
        serialNumber: values.serialNumber,
        assetType: values.assetType,
        assetTag: values.assetTag,
        manufacturer: values.manufacturer,
        model: values.model,
        assetState: values.assetState,
        assignedTo: values.assignedTo,
        location: values.location,
        department: values.department,
        region: values.region,
        assetDetails: [
            ...(values.model ? [{ key: AssetPathKey.MODEL, value: values.model }] : []),
            ...values.assetDetails
                .filter(({ key }) => key !== AssetPathKey.MODEL)
                .map(({ key, value }) => ({ key, value })),
            ...(values.parentAssetId && !hasParentAssetIdInDetails
                ? [{ key: PARENT_ASSET_ID_KEY, value: values.parentAssetId }]
                : []),
        ],
    };
};

interface Props {
    asset?: Asset;
    onEditDone?: (updated: Asset) => void;
    onCreateDone?: () => void;
    groupUuid?: string;
}

export const ManageAssetView = (props: Props) => {
    const { t } = useTranslation();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const isEdit = props.asset !== undefined;
    const [initialValues, setInitialValues] = React.useState<FormValues | undefined>(
        isEdit ? undefined : EMPTY_FORM_VALUES
    );
    const originalValuesRef = React.useRef<FormValues | undefined>(undefined);
    const [isLoading, setIsLoading] = React.useState(false);
    const [result, setResult] = React.useState<Result>({ title: t("Common.pleaseWait"), message: "" });
    const [isResultVisible, setIsResultVisible] = React.useState(false);
    const [submitFailed, setSubmitFailed] = React.useState(false);
    const [validateOnChange, setValidateOnChange] = React.useState(false);
    const abortControllerRef = React.useRef<AbortController | undefined>(undefined);
    const existingKeys = useExistingAssetKeys();
    const { closeSubPage } = useSubPage();
    const [selectedGroupUuid, setSelectedGroupUuid] = React.useState(props.groupUuid ?? "");
    const subPageContentRef = React.useRef<HTMLDivElement>(null);
    const isMobileViewport = useIsMobileViewport();

    React.useEffect(() => {
        if (!isEdit) {
            return;
        }
        const abortController = new AbortController();
        assetService
            .fetchAssetByUuid(props.asset!.uuid, abortController)
            .then(async (fullAsset) => {
                let parentAssetId = fullAsset.assetDetails?.find((d) => d.key === PARENT_ASSET_ID_KEY)?.value;

                if (!parentAssetId && fullAsset.parentUuid) {
                    try {
                        const parentAsset = await assetService.fetchAssetByUuid(fullAsset.parentUuid, abortController);
                        parentAssetId = parentAsset.assetId;
                    } catch (error) {
                        logger.debug("Failed to fetch parent asset details:", error);
                    }
                }

                const values = toFormValues(fullAsset, parentAssetId);
                originalValuesRef.current = values;
                setInitialValues(values);
            })
            .catch((error) => logger.error("Failed to fetch asset data for editing:", error));
        return () => abortController.abort();
    }, [props.asset?.uuid]);

    React.useEffect(
        () => () => {
            closeSubPage();
            abortControllerRef.current?.abort();
        },
        [closeSubPage]
    );

    const title = isEdit
        ? t("AssetManagement.editAssetSubpage.title", { assetId: props.asset!.assetId })
        : t("AssetManagement.createAssetSubpage.title");

    const hideResultModal = () => {
        setIsResultVisible(false);
        setResult({ title: t("Common.pleaseWait"), message: "" });
        if (!submitFailed) {
            closeSubPage();
        }
    };

    const submitHandler: FormikConfig<FormValues>["onSubmit"] = async (values, { setSubmitting }) => {
        if (
            isEdit &&
            props.asset &&
            (props.asset.hasChildren || props.asset.hasHiddenChildren) &&
            values.parentAssetId
        ) {
            setResult({
                title: t("AssetManagement.editAssetSubpage.cannotAssignParentTitle"),
                message: t("AssetManagement.editAssetSubpage.cannotAssignParentMessage"),
            });
            setIsResultVisible(true);
            setSubmitFailed(true);
            setSubmitting(false);
            return;
        }

        setIsLoading(true);
        setIsResultVisible(true);
        setSubmitFailed(false);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            if (isEdit) {
                usageStatisticsService.sendEvent({ category: Category.ASSET_MANAGEMENT, action: Action.EDIT_ASSET });
                await assetService.editAsset(
                    props.asset!.uuid,
                    toAssets(originalValuesRef.current!),
                    toAssets(values),
                    abortController
                );
                showToast(t("AssetManagement.editAssetSubpage.successMessage"), "success");
                props.onEditDone?.({
                    ...props.asset!,
                    serialNumber: values.serialNumber,
                    assetType: values.assetType,
                    assetTag: values.assetTag,
                    manufacturer: values.manufacturer,
                    model: values.model,
                    assetState: values.assetState,
                    assignedTo: values.assignedTo,
                    location: values.location,
                    department: values.department,
                    region: values.region,
                    assetDetails: values.assetDetails,
                });
            } else {
                usageStatisticsService.sendEvent({ category: Category.ASSET_MANAGEMENT, action: Action.ADD_ASSET });
                await assetService.createAsset(toAssets(values), abortController, selectedGroupUuid || undefined);
                showToast(t("AssetManagement.createAssetSubpage.successMessage"), "success");
                props.onCreateDone?.();
            }
            hideResultModal();
        } catch {
            setResult({
                title: isEdit
                    ? t("AssetManagement.editAssetSubpage.failureTitle")
                    : t("AssetManagement.createAssetSubpage.failureTitle"),
                message: isEdit
                    ? t("AssetManagement.editAssetSubpage.failureMessage")
                    : t("AssetManagement.createAssetSubpage.failureMessage"),
            });
            setSubmitFailed(true);
        } finally {
            setIsLoading(false);
            setSubmitting(false);
        }
    };

    const validationSchema = buildValidationSchema(t, translatedPaths, !isEdit);
    const isParentAsset = props.asset !== undefined && (props.asset.hasChildren || props.asset.hasHiddenChildren);

    const buildKeyItems = (currentKey: string, usedKeys: string[]) => {
        const knownKeys = existingKeys.filter((key) => !(isParentAsset && key === PARENT_ASSET_ID_KEY));
        const createdKeys = Array.from(new Set([currentKey, ...usedKeys])).filter(
            (key) => key !== "" && !knownKeys.includes(key)
        );
        return [...knownKeys, ...createdKeys].map((key) => ({
            value: key,
            label: translatedPaths(resolveAssetPathKey(key) ?? key),
            disabled: usedKeys.some((usedKey) => isSameAssetKey(usedKey, key)),
        }));
    };

    return (
        <>
            {initialValues === undefined ? (
                <SubPageLayout title={title} cancelButtonLabel={t("Common.cancel")}>
                    <LoadingIndicator />
                </SubPageLayout>
            ) : (
                <Formik<FormValues>
                    initialValues={initialValues}
                    enableReinitialize={isEdit}
                    onSubmit={submitHandler}
                    validationSchema={validationSchema}
                    validateOnChange={validateOnChange}
                    validateOnBlur={false}
                >
                    {(formikProps) => {
                        const hasSubmitted = formikProps.submitCount > 0;
                        const indexedDetails = formikProps.values.assetDetails
                            .map((detail, index) => ({ detail, index }))
                            .filter(({ detail }) => !MANDATORY_KEYS.has(detail.key));
                        const newEntries = indexedDetails.filter(({ detail }) => detail.isNew === true);
                        const ungroupedEntries = indexedDetails.filter(
                            ({ detail }) =>
                                !detail.isNew &&
                                !RISK_SCORE_KEYS.has(detail.key) &&
                                (!ALL_GROUPED_KEYS.has(detail.key) || detail.key === PARENT_ASSET_ID_KEY)
                        );

                        return (
                            <SubPageLayout
                                subPageContentRef={subPageContentRef}
                                title={title}
                                cancelButtonLabel={t("Common.cancel")}
                                buttons={
                                    <Button
                                        variant="Primary"
                                        type="submit"
                                        form="assetManageForm"
                                        disabled={isEdit && !formikProps.dirty}
                                        onClick={() => setValidateOnChange(true)}
                                        testId={testIds.workArea.asset.manageAssetView.submitButton}
                                    >
                                        {isEdit ? t("Common.save") : t("Common.create")}
                                    </Button>
                                }
                                footerBanner={{
                                    visible: hasSubmitted && !formikProps.isValid,
                                    text: t("Common.formValidationError"),
                                }}
                            >
                                <Form id="assetManageForm">
                                    <Box columnLayout gap={"Md"}>
                                        <Heading variant={"SUBTITLE_1"} disableTopSpacing disableBottomSpacing>
                                            {t("AssetManagement.createAssetSubpage.assetInfo")}
                                        </Heading>
                                        {!isEdit && (
                                            <GroupSelect
                                                name="groupSelect"
                                                value={selectedGroupUuid}
                                                onChange={setSelectedGroupUuid}
                                                disabled={!!props.groupUuid}
                                            />
                                        )}
                                        {!isEdit && (
                                            <TextInput
                                                label={translatedPaths(AssetPathKey.ASSET_ID)}
                                                labelAbove={isMobileViewport}
                                                name={"assetId"}
                                                id={"assetId"}
                                                value={formikProps.values.assetId}
                                                onChange={(event) =>
                                                    formikProps.setFieldValue("assetId", event.target.value)
                                                }
                                                error={hasSubmitted ? formikProps.errors.assetId : undefined}
                                                maxLength={INPUT_MAX_LENGTH}
                                                testIds={{
                                                    input: testIds.workArea.asset.manageAssetView.assetIdInput.itself,
                                                    error: testIds.workArea.asset.manageAssetView.assetIdInput
                                                        .errorLabel,
                                                }}
                                            />
                                        )}
                                        {EDITABLE_FIELDS.map(({ pathKey, name, testId, maxLength }) => (
                                            <TextInput
                                                key={name}
                                                label={translatedPaths(pathKey)}
                                                labelAbove={isMobileViewport}
                                                name={name}
                                                id={name}
                                                value={formikProps.values[name] as string}
                                                onChange={(event) =>
                                                    formikProps.setFieldValue(name, event.target.value)
                                                }
                                                error={
                                                    formikProps.touched[name] || hasSubmitted
                                                        ? (formikProps.errors[name] as string | undefined)
                                                        : undefined
                                                }
                                                maxLength={maxLength}
                                                testIds={{ input: testId.itself, error: testId.errorLabel }}
                                            />
                                        ))}
                                        {SECTION_GROUPS.map((group) => {
                                            const groupEntries = indexedDetails.filter(
                                                ({ detail }) =>
                                                    group.keys.includes(detail.key as AssetPathKey) &&
                                                    !detail.isNew &&
                                                    !BLANCCO_INTELLIGENCE_AND_GRADING_KEYS.has(detail.key)
                                            );
                                            if (groupEntries.length === 0) {
                                                return null;
                                            }
                                            return (
                                                <React.Fragment key={group.titleKey}>
                                                    <Divider direction={"Horizontal"} />
                                                    <Heading
                                                        variant={"SUBTITLE_1"}
                                                        disableTopSpacing
                                                        disableBottomSpacing
                                                    >
                                                        {t(group.titleKey)}
                                                    </Heading>
                                                    {groupEntries.map(({ detail, index }) => (
                                                        <TextInput
                                                            key={index}
                                                            label={translatedPaths(detail.key)}
                                                            name={`assetDetails[${index}].value`}
                                                            value={detail.value}
                                                            maxLength={CUSTOM_ASSET_FIELD_MAX_LENGTH}
                                                            onChange={formikProps.handleChange}
                                                            onBlur={formikProps.handleBlur}
                                                        />
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                        {ungroupedEntries.length > 0 && (
                                            <>
                                                <Divider direction={"Horizontal"} />
                                                <Heading variant={"SUBTITLE_1"} disableTopSpacing disableBottomSpacing>
                                                    {t("AssetManagement.editAssetSubpage.sections.otherDetails")}
                                                </Heading>
                                                {ungroupedEntries.map(({ detail, index }) => {
                                                    const fieldErrors = Array.isArray(formikProps.errors.assetDetails)
                                                        ? formikProps.errors.assetDetails[index]
                                                        : undefined;
                                                    const resolvedKey = resolveAssetPathKey(detail.key);
                                                    const label = resolvedKey
                                                        ? translatedPaths(resolvedKey)
                                                        : detail.key;
                                                    const isParentAssetIdField = detail.key === PARENT_ASSET_ID_KEY;
                                                    const isGradingActionField =
                                                        detail.key === GRADING_ACTION_DETAIL_KEY;
                                                    const isDisabled =
                                                        isParentAssetIdField &&
                                                        props.asset &&
                                                        (props.asset.hasChildren || props.asset.hasHiddenChildren);

                                                    if (isGradingActionField) {
                                                        return (
                                                            <Select
                                                                key={index}
                                                                label={label}
                                                                name={`assetDetails[${index}].value`}
                                                                placeholder={t(
                                                                    "AssetManagement.value.gradingTable.selectAction"
                                                                )}
                                                                selectedByDefault={detail.value || undefined}
                                                                items={GRADING_ACTION_KEYS.map((key) => ({
                                                                    value: key,
                                                                    label: t(
                                                                        `AssetManagement.value.gradingTable.actions.${key}`
                                                                    ),
                                                                }))}
                                                                onSelection={(value) =>
                                                                    formikProps.setFieldValue(
                                                                        `assetDetails[${index}].value`,
                                                                        value
                                                                    )
                                                                }
                                                                error={
                                                                    isFieldErrors(fieldErrors)
                                                                        ? fieldErrors.value
                                                                        : undefined
                                                                }
                                                            />
                                                        );
                                                    }

                                                    return (
                                                        <TextInput
                                                            key={index}
                                                            label={label}
                                                            name={`assetDetails[${index}].value`}
                                                            id={`assetDetails[${index}].value`}
                                                            value={detail.value}
                                                            maxLength={CUSTOM_ASSET_FIELD_MAX_LENGTH}
                                                            onChange={formikProps.handleChange}
                                                            onBlur={formikProps.handleBlur}
                                                            disabled={isDisabled}
                                                            error={
                                                                isFieldErrors(fieldErrors)
                                                                    ? fieldErrors.value
                                                                    : undefined
                                                            }
                                                        />
                                                    );
                                                })}
                                            </>
                                        )}
                                        <Divider direction={"Horizontal"} />
                                        <Heading variant={"SUBTITLE_1"} disableTopSpacing disableBottomSpacing>
                                            {t("AssetManagement.createAssetSubpage.assetDetails")}
                                        </Heading>
                                        {newEntries.length > 0 && (
                                            <Box className={style.staticTable}>
                                                <StaticTable
                                                    headers={[
                                                        {
                                                            value: t(
                                                                "AssetManagement.createAssetSubpage.customFields.key"
                                                            ),
                                                        },
                                                        {
                                                            value: t(
                                                                "AssetManagement.createAssetSubpage.customFields.value"
                                                            ),
                                                        },
                                                        { value: "" },
                                                    ]}
                                                    cells={newEntries.map(({ detail, index }) => {
                                                        const fieldErrors = Array.isArray(
                                                            formikProps.errors.assetDetails
                                                        )
                                                            ? formikProps.errors.assetDetails[index]
                                                            : undefined;
                                                        const usedKeys = formikProps.values.assetDetails
                                                            .filter((_, currentIndex) => currentIndex !== index)
                                                            .map((detailItem) => detailItem.key);
                                                        const availableItems = buildKeyItems(detail.key, usedKeys);
                                                        return [
                                                            <SearchSelect
                                                                width={isMobileViewport ? 220 : undefined}
                                                                containerRef={subPageContentRef}
                                                                flipOptions={{
                                                                    fallbackPlacements: ["top-start"],
                                                                }}
                                                                key={`select-key-${index}-${existingKeys.length}-${usedKeys.join("|")}`}
                                                                hideLabelSection
                                                                allowCreateOption
                                                                createOptionText={t(
                                                                    "AssetManagement.createAssetSubpage.createNewKey"
                                                                )}
                                                                closeOnSelection={true}
                                                                name={`assetDetails[${index}].key`}
                                                                placeholder={t(
                                                                    "AssetManagement.createAssetSubpage.searchOrSelectValues"
                                                                )}
                                                                dropdownMaxHeight={200}
                                                                items={availableItems}
                                                                onSelection={(value) =>
                                                                    formikProps.setFieldValue(
                                                                        `assetDetails[${index}].key`,
                                                                        String(value)
                                                                    )
                                                                }
                                                                onCreateOption={(value) =>
                                                                    formikProps.setFieldValue(
                                                                        `assetDetails[${index}].key`,
                                                                        resolveAssetPathKey(value) ?? value.trim()
                                                                    )
                                                                }
                                                                selectedByDefault={detail.key || undefined}
                                                                error={
                                                                    hasSubmitted && isFieldErrors(fieldErrors)
                                                                        ? fieldErrors.key
                                                                        : undefined
                                                                }
                                                                testIds={{
                                                                    input: testIds.workArea.asset.manageAssetView
                                                                        .customTable.keySearchSelect.itself,
                                                                    error: testIds.workArea.asset.manageAssetView
                                                                        .customTable.keySearchSelect.errorLabel,
                                                                }}
                                                            />,
                                                            <TextInput
                                                                key={`value-${index}`}
                                                                hideLabelSection
                                                                type="text"
                                                                width={isMobileViewport ? 220 : undefined}
                                                                name={`assetDetails[${index}].value`}
                                                                value={detail.value}
                                                                maxLength={CUSTOM_ASSET_FIELD_MAX_LENGTH}
                                                                onChange={formikProps.handleChange}
                                                                onBlur={formikProps.handleBlur}
                                                                error={
                                                                    hasSubmitted && isFieldErrors(fieldErrors)
                                                                        ? fieldErrors.value
                                                                        : undefined
                                                                }
                                                                testIds={{
                                                                    input: testIds.workArea.asset.manageAssetView
                                                                        .customTable.valueInput.itself,
                                                                    error: testIds.workArea.asset.manageAssetView
                                                                        .customTable.valueInput.errorLabel,
                                                                }}
                                                            />,
                                                            <IconButton
                                                                key={`delete-${index}`}
                                                                onClick={() => {
                                                                    const updated = [
                                                                        ...formikProps.values.assetDetails,
                                                                    ];
                                                                    updated.splice(index, 1);
                                                                    formikProps.setFieldValue("assetDetails", updated);
                                                                }}
                                                                data-testid={
                                                                    testIds.workArea.asset.manageAssetView.customTable
                                                                        .removeButton
                                                                }
                                                            >
                                                                <Delete />
                                                            </IconButton>,
                                                        ];
                                                    })}
                                                    testId={testIds.workArea.asset.manageAssetView.customTable.itself}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                    <Box paddingTop={"Md"}>
                                        <Button
                                            type="button"
                                            icon={<AddOutline color={resolveColor("--theme-primary")} />}
                                            variant="Tertiary"
                                            onClick={() => addRow(formikProps, hasSubmitted)}
                                            data-testid={testIds.workArea.asset.manageAssetView.customTable.addButton}
                                        >
                                            {t("AssetManagement.createAssetSubpage.addNewField")}
                                        </Button>
                                    </Box>
                                </Form>
                            </SubPageLayout>
                        );
                    }}
                </Formik>
            )}
            <Modal
                isOpen={isResultVisible}
                hideModal={hideResultModal}
                title={result.title}
                hideCloseIcon={true}
                buttons={
                    !isLoading && (
                        <Button
                            variant="Primary"
                            onClick={hideResultModal}
                            data-testid={testIds.common.dialog.closeButton}
                        >
                            {t("Common.ok")}
                        </Button>
                    )
                }
            >
                {isLoading ? <LoadingIndicator /> : result.message}
            </Modal>
        </>
    );
};
